var _excluded = ["isOpen", "onClose", "onCloseComplete", "placement", "showCloseButton", "isLoading", "closeButtonAriaLabel", "children", "shouldAutoFocus"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { FloatingFocusManager, useDismiss, useFloating, useInteractions, useMergeRefs, useRole } from '@floating-ui/react';
import { useCallback } from 'react';
import { LoadingContainer } from '../../../containers';
import { Slide } from '../../../foundations/transitions';
import { themeSpaces, zIndices } from '../../../theme/foundations';
import { forwardRef } from '../../../utils';
import { useMultiStyleConfig } from '../../../utils/use-style-config';
import { IconButton } from '../../action';
import { Blanket } from '../../feedback/Blanket';
import { Box } from '../Box';
import { Portal } from '../Portal';
/**
 * @private For internal use only.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

/**
 * @private For internal use only.
 */
export var _BaseSheet = forwardRef((_ref, propRef) => {
  var {
    isOpen,
    onClose,
    onCloseComplete,
    placement = 'left',
    showCloseButton = false,
    isLoading = false,
    closeButtonAriaLabel = 'Close sheet',
    children,
    shouldAutoFocus = false
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var {
    refs,
    context
  } = useFloating({
    open: isOpen,
    onOpenChange: onClose
  });
  var ref = useMergeRefs([refs.setFloating, propRef]);
  var role = useRole(context);
  var dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown'
  });
  var {
    getFloatingProps
  } = useInteractions([role, dismiss]);
  var onAnimationComplete = useCallback(definition => {
    if (definition === 'enter') return;
    onCloseComplete === null || onCloseComplete === void 0 ? void 0 : onCloseComplete(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var styles = useMultiStyleConfig('_BaseSheet', {
    placement,
    showCloseButton
  });
  return /*#__PURE__*/_jsxs(Portal, {
    children: [/*#__PURE__*/_jsx(Blanket, {
      isOpen: isOpen,
      isFullScreen: true,
      "data-testid": "base-sheet-overlay"
    }), /*#__PURE__*/_jsx(Slide, {
      direction: placement,
      in: isOpen,
      style: {
        zIndex: zIndices.modal,
        width: placement === 'bottom' ? '100vw' : "calc(100vw - ".concat(themeSpaces.xxxl, ")"),
        maxWidth: placement === 'bottom' ? '100vw' : '375px',
        maxHeight: placement === 'bottom' ? "calc(100vh - ".concat(themeSpaces.l, ")") : '100vh'
      },
      onAnimationComplete: onAnimationComplete,
      transition: {
        enter: {
          duration: 0.3
        },
        exit: {
          duration: 0.3
        }
      },
      children: /*#__PURE__*/_jsx(FloatingFocusManager, {
        context: context,
        visuallyHiddenDismiss: !showCloseButton // Focus the close button first if it's visible.
        ,
        initialFocus: shouldAutoFocus || showCloseButton ? 0 : -1,
        children: /*#__PURE__*/_jsxs(Box, _objectSpread(_objectSpread(_objectSpread({
          __css: styles['mainContainer'],
          "data-component": "_BaseSheet",
          ref: ref
        }, props), getFloatingProps()), {}, {
          children: [showCloseButton && /*#__PURE__*/_jsx(Box, {
            __css: styles['closeButtonContainer'],
            children: /*#__PURE__*/_jsx(IconButton, {
              onClick: onClose,
              variant: "naked",
              icon: "close",
              "aria-label": closeButtonAriaLabel
            })
          }), isLoading ? /*#__PURE__*/_jsx(Box, {
            __css: styles['loadingContainer'],
            children: /*#__PURE__*/_jsx(LoadingContainer, {
              isLoading: true
            })
          }) : children]
        }))
      })
    })]
  });
});
_BaseSheet.displayName = '_BaseSheet';
//# sourceMappingURL=_BaseSheet.js.map